/**
 * De afrekening na afsluiting van een evenement.
 *
 * Dit is de belofte: iedereen zijn geld binnen één werkdag, en tot op de cent
 * kloppend. De volgorde staat vast en is dezelfde als op het verkoopoverzicht
 * dat de kraam krijgt, zodat hij kan controleren wat wij namens hem hebben
 * opgemaakt (selfbilling).
 *
 * Twee dingen die hier bewust anders zijn dan in het oorspronkelijke plan:
 *
 *  - De doorbelaste transactiekosten gaan van de omzet van de kraam af en
 *    staan NIET op de factuur aan de organisator. Ze twee keer meenemen zou ze
 *    dubbel incasseren.
 *  - Het statiegeldsaldo blijft bij wie de kraam of bar draait. Draait de
 *    organisator hem zelf, dan gaat die hele afrekening naar de organisator.
 */

import {
  btwOverzicht,
  telBtwOpBijNetto,
  BTW_HOOG,
  type BtwOverzicht,
  type BtwRegel,
} from './btw.js';
import type { CommissieResultaat } from './commissie.js';
import { sommeer, type Basispunten, type Cent } from './geld.js';
import { berekenKraamsetkosten, type Prijsmodel } from './prijsmodel.js';
import {
  berekenUitbetalingKraam,
  berekenUitbetalingOrganisator,
  controleerAfrekening,
  type Afrekeningregel,
  type AfrekeningControle,
  type Uitbater,
  type UitbetalingKraam,
  type UitbetalingOrganisator,
} from './uitbetaling.js';

export interface KraamafrekeningInvoer {
  kraamdeelnameId: string;
  kraamnaam: string;
  /** Waar het geld heen gaat. Bij uitbater 'organisator' is dit diens IBAN. */
  iban: string;
  uitbater?: Uitbater;
  commissieBasispunten: Basispunten;
  /** Bruto omzet per BTW-tarief, statiegeld zit hier niet in. */
  omzetPerTarief: readonly BtwRegel[];
  aantalBonnen: number;
  retourenCent?: Cent;
  correctiesCent?: Cent;
  statiegeldSaldoCent?: Cent;
  transactiekostenCent?: Cent;
  inhoudingenCent?: Cent;
  bufferBasispunten?: Basispunten;
}

export interface Kraamafrekening {
  kraamdeelnameId: string;
  kraamnaam: string;
  iban: string;
  ontvanger: Uitbater;
  aantalBonnen: number;
  /** De omzet gesplitst per tarief; statiegeld valt hierbuiten. */
  btw: BtwOverzicht;
  brutoOmzetCent: Cent;
  retourenCent: Cent;
  correctiesCent: Cent;
  statiegeldSaldoCent: Cent;
  transactiekostenCent: Cent;
  inhoudingenCent: Cent;
  commissie: CommissieResultaat;
  uitbetaling: UitbetalingKraam;
  /** Wat er daadwerkelijk bij deze kraam is afgerekend door bezoekers. */
  ontvangenCent: Cent;
}

export function maakKraamafrekening(invoer: KraamafrekeningInvoer): Kraamafrekening {
  const btw = btwOverzicht(invoer.omzetPerTarief);
  const brutoOmzetCent = btw.totaalBrutoCent;
  const retourenCent = invoer.retourenCent ?? 0;
  const correctiesCent = invoer.correctiesCent ?? 0;
  const statiegeldSaldoCent = invoer.statiegeldSaldoCent ?? 0;
  const transactiekostenCent = invoer.transactiekostenCent ?? 0;
  const inhoudingenCent = invoer.inhoudingenCent ?? 0;

  const uitbetaling = berekenUitbetalingKraam({
    brutoOmzetCent,
    retourenCent,
    correctiesCent,
    statiegeldSaldoCent,
    commissieBasispunten: invoer.commissieBasispunten,
    transactiekostenCent,
    inhoudingenCent,
    bufferBasispunten: invoer.bufferBasispunten ?? 0,
    uitbater: invoer.uitbater ?? 'kraam',
  });

  return {
    kraamdeelnameId: invoer.kraamdeelnameId,
    kraamnaam: invoer.kraamnaam,
    iban: invoer.iban,
    ontvanger: uitbetaling.ontvanger,
    aantalBonnen: invoer.aantalBonnen,
    btw,
    brutoOmzetCent,
    retourenCent,
    correctiesCent,
    statiegeldSaldoCent,
    transactiekostenCent,
    inhoudingenCent,
    commissie: uitbetaling.commissie,
    uitbetaling,
    // Wat de bezoekers bij deze kraam hebben afgerekend: de grondslag plus het
    // statiegeld dat erin zat.
    ontvangenCent: uitbetaling.grondslagCent + statiegeldSaldoCent,
  };
}

export interface EvenementafrekeningInvoer {
  evenementnaam: string;
  organisatornaam: string;
  organisatorIban: string;
  prijsmodel: Prijsmodel;
  aantalKraamsets: number;
  aantalDagen: number;
  kramen: readonly KraamafrekeningInvoer[];
}

export interface Evenementafrekening {
  kramen: Kraamafrekening[];
  organisator: UitbetalingOrganisator;
  /** Onze factuur aan de organisator: de kraamsets per dag. */
  kraamsetkosten: ReturnType<typeof telBtwOpBijNetto>;
  commissieTotaalCent: Cent;
  transactiekostenTotaalCent: Cent;
  inhoudingenTotaalCent: Cent;
  /** Wat er in totaal bij bezoekers is afgerekend. */
  ontvangenCent: Cent;
  /** Wat Quality Cashless overhoudt: doorbelaste kosten plus de kraamsets. */
  eigenOpbrengstCent: Cent;
  posten: Afrekeningregel[];
  controle: AfrekeningControle;
}

/**
 * Rekent het hele evenement af en controleert of het sluit.
 *
 * De controle is geen formaliteit: pas als het verschil nul is mag het
 * SEPA-bestand de deur uit.
 */
export function maakEvenementafrekening(invoer: EvenementafrekeningInvoer): Evenementafrekening {
  const kramen = invoer.kramen.map(maakKraamafrekening);

  const commissieTotaalCent = sommeer(kramen.map((kraam) => kraam.commissie.commissieCent));
  const transactiekostenTotaalCent = sommeer(kramen.map((kraam) => kraam.transactiekostenCent));
  const inhoudingenTotaalCent = sommeer(kramen.map((kraam) => kraam.inhoudingenCent));
  const ontvangenCent = sommeer(kramen.map((kraam) => kraam.ontvangenCent));

  const kraamsetkosten = telBtwOpBijNetto(
    berekenKraamsetkosten(invoer.prijsmodel, invoer.aantalKraamsets, invoer.aantalDagen),
    BTW_HOOG,
  );

  const organisator = berekenUitbetalingOrganisator({
    commissieTotaalCent,
    onzeFactuurBrutoCent: kraamsetkosten.brutoCent,
  });

  const eigenOpbrengstCent =
    transactiekostenTotaalCent + kraamsetkosten.brutoCent + inhoudingenTotaalCent;

  const posten: Afrekeningregel[] = [
    ...kramen.map((kraam) => ({
      omschrijving: `${kraam.kraamnaam}${kraam.ontvanger === 'organisator' ? ' (naar de organisator)' : ''}`,
      bedragCent: kraam.uitbetaling.totaalUitTeBetalenCent,
    })),
    {
      omschrijving: `${invoer.organisatornaam} — commissie`,
      bedragCent: organisator.uitTeBetalenCent,
    },
    {
      omschrijving: 'Quality Cashless — doorbelaste transactiekosten',
      bedragCent: transactiekostenTotaalCent,
    },
    { omschrijving: 'Quality Cashless — kraamsets', bedragCent: kraamsetkosten.brutoCent },
  ];
  if (inhoudingenTotaalCent !== 0) {
    posten.push({
      omschrijving: 'Quality Cashless — inhoudingen',
      bedragCent: inhoudingenTotaalCent,
    });
  }

  return {
    kramen,
    organisator,
    kraamsetkosten,
    commissieTotaalCent,
    transactiekostenTotaalCent,
    inhoudingenTotaalCent,
    ontvangenCent,
    eigenOpbrengstCent,
    posten,
    controle: controleerAfrekening(ontvangenCent, posten),
  };
}
